// Page: Websites
// Purpose: App page to browse and preview websites with search, tag
// filter, saved-only view, infinite scroll, and modal preview.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import WebsiteFilters from '../../components/templates/templates/WebsiteFilters';
import WebsiteGrid from '../../components/templates/templates/WebsiteGrid';
import WebsiteModal from '../../components/templates/templates/WebsiteModal';
import { getAllWebsites, saveWebsite, unsaveWebsite, getSavedWebsiteIds } from '../../api/websites/websitesApi';
import { useAuth } from '../../context/auth';
import { useNamespace } from '../../context/namespace';
import { useToast } from '../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../components/shared/Footer';
import { AlertCircle, Globe, Search, X, Loader2 } from 'lucide-react';
import { WEBSITES_PER_PAGE } from '../../data/limits/websiteLimits';
import SEOHead from '../../components/shared/SEOHead';

// Add keyframe animation for fade-in
const fadeInAnimation = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

// Website Search Component
const WebsiteSearch = ({ 
  searchQuery, 
  setSearchQuery, 
  onSearch, 
  onClearSearch, 
  isLoading
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);

  // Sync inputValue with searchQuery when searchQuery changes from outside
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const clearSearch = () => {
    setInputValue('');
    if (onClearSearch) {
      onClearSearch();
    } else {
      setSearchQuery('');
      onSearch('');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(inputValue);
    onSearch(inputValue);
  };

  return (
    <div className="relative mb-6">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 py-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Search websites..."
            value={inputValue}
            onChange={handleSearchChange}
          />
        </div>
        <button
          type="submit"
          disabled={!inputValue.trim() || isLoading || inputValue.trim() === searchQuery.trim()}
          className={`px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors flex items-center gap-2 ${
            !inputValue.trim() || inputValue.trim() === searchQuery.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
              : isLoading
                ? 'bg-primary-500 text-white cursor-wait'
                : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <span>Search</span>
          )}
        </button>
        
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-3 py-2.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center transition-colors"
            title="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </form>
    </div>
  );
};

function Websites() {
  
  // Add the style tag for animations
  useEffect(() => {
    // Record websites page view (non-blocking)
    (async () => {
      try {
        const { post } = await import('../../api/core');
        await post('/websites/page-view', {});
      } catch (_) {}
    })();
    // Add the animation styles to the document head
    const styleTag = document.createElement('style');
    styleTag.innerHTML = fadeInAnimation;
    document.head.appendChild(styleTag);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState('random');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchBarLoading, setSearchBarLoading] = useState(false);
  const [showSavedWebsites, setShowSavedWebsites] = useState(false);
  const [savedWebsiteIds, setSavedWebsiteIds] = useState(new Set());
  const [savedWebsitesLoaded, setSavedWebsitesLoaded] = useState(false);
  const [loadingSavedWebsites, setLoadingSavedWebsites] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  // State for API data and pagination
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [allWebsitesLoaded, setAllWebsitesLoaded] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  
  // Get auth context and toast
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Get namespace context
  const { namespaces, currentNamespace } = useNamespace();
  
  // Find the current namespace ID from the namespaces array
  const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  // Get user's plan
  const userPlan = user?.subscriptionStatus?.currentPlanLevel || user?.planLevel || 'basic';
  const isPremiumUser = userPlan === 'premium';
  
  // Refs for scroll handling and API request tracking
  const lastLoadTimeRef = useRef(0);
  const apiRequestInProgressRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  const requestCountRef = useRef(0);
  const sentinelRef = useRef(null);
  
  // Add intersection observer refs  
  const loadingMoreRef = useRef(false);

  // Load saved website IDs
  const loadSavedWebsiteIds = async () => {
    if (!user || !currentNamespaceId || loadingSavedWebsites) return;

    try {
      setLoadingSavedWebsites(true);
      console.log(`[Websites] Fetching saved websites for namespace: ${currentNamespace} (${currentNamespaceId})`);
      const savedIds = await getSavedWebsiteIds(currentNamespaceId);
      console.log(`[Websites] Found ${savedIds.length} saved websites in namespace:`, savedIds);
      setSavedWebsiteIds(new Set(savedIds || []));
      setSavedWebsitesLoaded(true);
    } catch (error) {
      console.error('Error loading saved website IDs:', error);
      setSavedWebsitesLoaded(true);
      setSavedWebsiteIds(new Set());
    } finally {
      setLoadingSavedWebsites(false);
    }
  };

  // Initial load of saved websites
  useEffect(() => {
    if (user && currentNamespaceId) {
      loadSavedWebsiteIds();
    } else {
      // Clear saved websites when logged out or no namespace
      setSavedWebsiteIds(new Set());
      setSavedWebsitesLoaded(true);
    }
  }, [user, currentNamespace, currentNamespaceId]);

  // Load websites from API
  const loadWebsites = useCallback(async (resetList = false) => {
    console.log(`[Websites] Starting load - Reset: ${resetList}, Page: ${page}, Loading: ${loading}, AllLoaded: ${allWebsitesLoaded}`);
    
    // Handle reset first - this clears blocking states
    if (resetList) {
      setPage(1);
      setWebsites([]);
      setAllWebsitesLoaded(false);
      setHasMore(true);
      setError(null);
      setContentReady(false);
      // Reset API tracking
      apiRequestInProgressRef.current = false;
      lastRequestParamsRef.current = null;
    }
    
    // Only check blocking conditions if not resetting
    if (!resetList && (loading || allWebsitesLoaded)) {
      console.log('[Websites] ❌ Skipping load - already loading or all loaded');
      return;
    }

    // Prevent duplicate API calls
    const currentParams = JSON.stringify({
      page: resetList ? 1 : page,
      search: searchQuery,
      tags: activeFilter || '',
      sortBy: sortMethod
    });
    
    if (!resetList && apiRequestInProgressRef.current && lastRequestParamsRef.current === currentParams) {
      console.log('[Websites] ❌ Duplicate request prevented');
      return;
    }

    // Rate limiting - prevent requests more frequent than 500ms (but allow resets)
    const now = Date.now();
    if (!resetList && now - lastLoadTimeRef.current < 500) {
      console.log('[Websites] ❌ Rate limited');
      return;
    }

    apiRequestInProgressRef.current = true;
    lastRequestParamsRef.current = currentParams;
    requestCountRef.current++;
    
    const requestId = requestCountRef.current;
    console.log(`[Websites] 🚀 Starting API request #${requestId}`);

    setLoading(true);

    try {
      // Load saved website IDs if needed
      if (showSavedWebsites && !savedWebsitesLoaded && currentNamespaceId) {
        await loadSavedWebsiteIds();
      }

      const requestOptions = {
        page: resetList ? 1 : page,
        limit: WEBSITES_PER_PAGE,
        search: searchQuery,
        tags: activeFilter || '',
        sortBy: sortMethod
      };
      
      console.log(`[Websites] 📡 API Request #${requestId} with options:`, requestOptions);
      
      const result = await getAllWebsites(requestOptions);
      
      // Check if this is still the latest request
      if (requestCountRef.current !== requestId) {
        console.log(`[Websites] ⚠️ Request #${requestId} is stale, ignoring results`);
        return;
      }
      
      // Validate API response structure
      if (!result || typeof result !== 'object') {
        console.error(`[Websites] ❌ Invalid API response structure:`, result);
        setError('Invalid response from server. Please try again.');
        return;
      }
      
      console.log(`[Websites] ✅ Received ${result.websites?.length || 0} websites`);
      
      if (result.websites && Array.isArray(result.websites) && result.websites.length > 0) {
        let newWebsites = result.websites;

        // Filter by saved websites if needed
        if (showSavedWebsites) {
          newWebsites = newWebsites.filter(website => savedWebsiteIds.has(website.id));
        }

        if (resetList) {
          setWebsites(newWebsites);
        } else {
          // Use functional update to avoid dependency on websites
          setWebsites(prev => {
            const existingIds = new Set(prev.map(website => website.id));
            const uniqueNewWebsites = newWebsites.filter(website => !existingIds.has(website.id));
            
            if (uniqueNewWebsites.length > 0) {
              return [...prev, ...uniqueNewWebsites];
            }
            return prev;
          });
          
          // Always increment page when not resetting, as we fetched a new page
          setPage(prev => prev + 1);
        }
        
        // Check if we've loaded all websites
        if (result.websites.length < WEBSITES_PER_PAGE) {
          console.log('[Websites] All websites loaded (less than limit returned)');
          setHasMore(false);
          setAllWebsitesLoaded(true);
        } else {
          setHasMore(true);
        }
      } else {
        console.log('[Websites] No websites returned');
        if (resetList) {
          setWebsites([]);
        }
        setHasMore(false);
        setAllWebsitesLoaded(true);
      }
      
      setIsDataReady(true);
      
    } catch (err) {
      console.error('[Websites] ❌ Error loading websites:', err);
      setError('Failed to load websites. ' + (err.message || ''));
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setSearchLoading(false);
      setSearchBarLoading(false);
      apiRequestInProgressRef.current = false;
      lastLoadTimeRef.current = Date.now();
      
      if (resetList) {
        setPage(2); // Set to 2 for next load
      }
      
      // Set content ready after a brief delay to ensure rendering is complete
      setTimeout(() => {
        setContentReady(true);
      }, 50);
    }
  }, [page, searchQuery, activeFilter, sortMethod, loading, allWebsitesLoaded, showSavedWebsites, savedWebsiteIds, savedWebsitesLoaded, currentNamespaceId]);

  // Function to load more websites (for infinite scroll)
  const loadMoreWebsites = useCallback(async () => {
    if (
      loading || 
      !hasMore || 
      allWebsitesLoaded || 
      apiRequestInProgressRef.current || 
      loadingMoreRef.current
    ) {
      console.log('[Websites] Skipping loadMore - conditions not met');
      return;
    }
    
    // Mark as loading more locally
    loadingMoreRef.current = true;
    
    console.log(`[Websites] 🔄 Loading more websites`);
    
    try {
      await loadWebsites(false);
    } catch (err) {
      console.error('[Websites] ❌ Error loading more websites:', err);
      setError('Failed to load more websites. ' + (err.message || ''));
    } finally {
      loadingMoreRef.current = false;
    }
  }, [loading, hasMore, allWebsitesLoaded, loadWebsites]);

  // Initial load when component mounts and saved websites are loaded
  useEffect(() => {
    // Only auto-load if we haven't loaded any data yet and saved websites are loaded
    if (!isDataReady && !loading && savedWebsitesLoaded) {
      console.log('[Websites] 🚀 Initial load triggered');
      loadWebsites(true);
    }
  }, [savedWebsitesLoaded, isDataReady, loading]);

  // Load websites when filters change (but not on initial mount)
  useEffect(() => {
    // Only load if we have already loaded websites initially (isDataReady = true)
    if (isDataReady) {
      console.log('[Websites] 🚀 useEffect triggered with:', {
        searchQuery,
        activeFilter,
        sortMethod,
        showSavedWebsites
      });
      console.log('[Websites] Filter change load effect triggered');
      loadWebsites(true);
    }
  }, [searchQuery, activeFilter, sortMethod, showSavedWebsites]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Only set up observer when content is ready and websites are loaded
    if (!isDataReady || !contentReady || websites.length === 0) {
      console.log('[Websites] 👁️ Not setting up observer - content not ready', {
        isDataReady,
        contentReady,
        websitesLength: websites.length
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        console.log('[Websites] 📏 Intersection event:', {
          isIntersecting: target.isIntersecting,
          hasMore,
          loading,
          allWebsitesLoaded,
          apiRequestInProgress: apiRequestInProgressRef.current,
          loadingMore: loadingMoreRef.current
        });
        
        if (target.isIntersecting && hasMore && !loading && !allWebsitesLoaded && !apiRequestInProgressRef.current) {
          console.log('[Websites] 📏 Sentinel element is visible, loading more websites');
          loadMoreWebsites();
        }
      },
      {
        root: null,
        rootMargin: '1500px 0px', // Load more when within 1500px of viewport bottom
        threshold: 0,
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
      console.log('[Websites] 👁️ Observer attached to sentinel element');
    } else {
      console.log('[Websites] ⚠️ Sentinel element not found, retrying in 100ms...');
      // Retry after a short delay to handle timing issues
      const retryTimer = setTimeout(() => {
        if (sentinelRef.current) {
          observer.observe(sentinelRef.current);
          console.log('[Websites] 👁️ Observer attached to sentinel element (retry)');
        } else {
          console.log('[Websites] ❌ Sentinel element still not found after retry');
        }
      }, 100);
      
      return () => {
        clearTimeout(retryTimer);
        if (sentinelRef.current) {
          observer.unobserve(sentinelRef.current);
        }
      };
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
        console.log('[Websites] 👁️ Observer detached from sentinel element');
      }
    };
  }, [loadMoreWebsites, hasMore, loading, allWebsitesLoaded, isDataReady, contentReady, websites.length]);

  const handleWebsiteClick = (website) => {
    setSelectedWebsite(website);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedWebsite(null);
  };

  const handleSearch = (query) => {
    console.log('[Websites] 🔍 handleSearch called with query:', query);
    // Only trigger loading state if the search query actually changed
    if (query !== searchQuery) {
      setSearchQuery(query);
      setSearchLoading(true);
      setSearchBarLoading(true);
      setContentReady(false);
    }
  };

  const handleClearSearch = () => {
    console.log('[Websites] 🔍 handleClearSearch called');
    setSearchQuery('');
    setSearchLoading(false);
    setSearchBarLoading(false);
  };

  const handleSortChange = (newSortMethod) => {
    console.log('[Websites] 🔀 handleSortChange called with:', newSortMethod);
    // Only trigger loading state if the sort method actually changed
    if (newSortMethod !== sortMethod) {
      setSortMethod(newSortMethod);
      setSearchLoading(true);
      setContentReady(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    console.log('[Websites] 🏷️ handleFilterChange called with:', newFilter);
    // Only trigger loading state if the filter value actually changed
    if (newFilter !== activeFilter) {
      setActiveFilter(newFilter);
      setSearchLoading(true);
      setContentReady(false);
    }
  };

  // Handle save/unsave website
  const handleSaveWebsite = async (websiteId, shouldSave) => {
    if (!user) {
      return; // Silent failure
    }

    if (!currentNamespaceId) {
      return; // Silent failure
    }

    // Optimistically update UI first
    if (shouldSave) {
      setSavedWebsiteIds(prev => new Set([...prev, websiteId]));
    } else {
      setSavedWebsiteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(websiteId);
        return newSet;
      });
    }

    try {
      if (shouldSave) {
        await saveWebsite(websiteId, currentNamespaceId);
      } else {
        await unsaveWebsite(websiteId, currentNamespaceId);
      }
      
      // If we're viewing saved websites, refresh the list
      if (showSavedWebsites) {
        loadWebsites(true);
      }
    } catch (error) {
      console.error('Error saving/unsaving website:', error);
      
      // Revert the UI change on error
      if (shouldSave) {
        setSavedWebsiteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(websiteId);
          return newSet;
        });
      } else {
        setSavedWebsiteIds(prev => new Set([...prev, websiteId]));
      }
    }
  };

  const handleToggleSavedFilter = () => {
    // If trying to see saved websites but no namespace is selected
    if (!showSavedWebsites && !currentNamespaceId) {
      return; // Silent failure
    }
    
    const newValue = !showSavedWebsites;
    setShowSavedWebsites(newValue);
    setSearchLoading(true);
    setContentReady(false);
    
    // When turning off the saved filter, refresh saved website IDs from the backend
    if (!newValue) {
      if (user && currentNamespaceId) {
        getSavedWebsiteIds(currentNamespaceId).then(savedIds => {
          console.log(`[Websites] Refreshing saved website IDs when disabling saved filter:`, savedIds);
          setSavedWebsiteIds(new Set(savedIds || []));
        }).catch(err => {
          console.error('[Websites] Error refreshing saved website IDs:', err);
          setSavedWebsiteIds(new Set());
        });
      } else {
        setSavedWebsiteIds(new Set());
      }
    }
  };

  // Calculate the number of columns for the skeleton based on screen width
  const getSkeletonColumns = () => {
    const width = window.innerWidth;
    if (width < 1024) return 2; // Below lg: 2 columns
    return 3; // lg and up: 3 columns
  };

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead 
        pageType="app-websites"
      />
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Websites
            </h1>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <p>Browse our collection of websites</p>
          </div>
        </div>
      </div>
      
      {/* Search Component - on its own line */}
      <div className="mb-4">
        <WebsiteSearch 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          isLoading={searchBarLoading}
        />
      </div>
      
      {/* Filter Component - on its own line */}
      <div className="mb-6">
        <WebsiteFilters 
          activeFilter={activeFilter} 
          setActiveFilter={handleFilterChange} 
          sortMethod={sortMethod}
          setSortMethod={handleSortChange}
          searchLoading={searchLoading}
          isPremiumUser={isPremiumUser}
          showSavedWebsites={showSavedWebsites}
          setShowSavedWebsites={handleToggleSavedFilter}
        />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {/* Manual load button when data hasn't been loaded yet */}
      {!isDataReady && !initialLoading && !loading && (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center mb-4">
            <Globe className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Ready to explore websites?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mb-6">
            Click the button below to load our collection of websites.
          </p>
          <button
            onClick={() => {
              setInitialLoading(true);
              loadWebsites(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Load Websites
          </button>
        </div>
      )}
      
      {/* Clean, centered loading indicator for initial loading OR active search/filter */}
      {(initialLoading || (loading && page === 1) || searchLoading || !contentReady) && (
        <div className="space-y-6">
          {/* Show skeleton grid immediately for better perceived performance */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {Array.from({ length: 6 }, (_, index) => (
              <div key={`skeleton-${index}`} className="animate-pulse">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-lg" style={{ aspectRatio: '16 / 9' }}></div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Grid Component - show for all users when not in skeleton loading state */}
      {!initialLoading && !(loading && page === 1) && !searchLoading && contentReady && (
        <>
          {websites.length > 0 ? (
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <WebsiteGrid
                websites={websites}
                onWebsiteClick={handleWebsiteClick}
                onSaveWebsite={handleSaveWebsite}
                savedWebsiteIds={savedWebsiteIds}
                loading={loading && page > 1}
                skeletonItems={[]}
                isPremiumUser={isPremiumUser}
              />
              
              {/* Sentinel element for infinite scroll */}
              {hasMore && !allWebsitesLoaded && (
                <div 
                  ref={sentinelRef}
                  className="h-10 my-6 flex justify-center" 
                >
                  {loading && (
                    <div className="flex justify-center items-center py-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-100 dark:border-gray-800 border-b-purple-600 dark:border-b-purple-400"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Only show "No Websites Found" if we have confirmed data state and no websites
            isDataReady && (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Websites Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  {searchQuery || activeFilter || showSavedWebsites
                    ? 'Try adjusting your search or filter criteria to find websites.'
                    : 'Our website collection is being updated. Please check back soon for new websites.'}
                </p>
              </div>
            )
          )}
        </>
      )}

      {/* Website Modal */}
      {isModalOpen && selectedWebsite && (
        <WebsiteModal
          website={selectedWebsite}
          isOpen={isModalOpen}
          onClose={closeModal}
          isSaved={savedWebsiteIds.has(selectedWebsite.id)}
          onSaveWebsite={handleSaveWebsite}
          isLoggedIn={!!user}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Websites;

